import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Euro,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { LeadStatus } from "./SalesPipeline";

// ─── Forecast config ─────────────────────────────────────────────────────────

const STAGE_PROBABILITY: Record<LeadStatus, number> = {
  PROSPECT: 0.1,
  DEMO: 0.3,
  NEGOCIATION: 0.65,
  GAGNE: 1.0,
  PERDU: 0.0,
};

const STAGE_CONFIG: Record<
  LeadStatus,
  { label: string; color: string; bgColor: string; badgeVariant: "default" | "secondary" | "destructive" | "outline" }
> = {
  PROSPECT: { label: "Prospect (10%)", color: "text-primary", bgColor: "bg-primary/5", badgeVariant: "outline" },
  DEMO: { label: "Démo (30%)", color: "text-pipeline-demo", bgColor: "bg-pipeline-demo/10", badgeVariant: "secondary" },
  NEGOCIATION: { label: "Négociation (65%)", color: "text-warning", bgColor: "bg-warning/5", badgeVariant: "default" },
  GAGNE: { label: "Gagné (100%)", color: "text-success", bgColor: "bg-success/5", badgeVariant: "default" },
  PERDU: { label: "Perdu (0%)", color: "text-destructive", bgColor: "bg-destructive/5", badgeVariant: "destructive" },
};

const BAR_COLORS: Record<string, string> = {
  PROSPECT: "bg-primary",
  DEMO: "bg-pipeline-demo",
  NEGOCIATION: "bg-warning",
  GAGNE: "bg-success",
};

const FORECAST_STAGES: LeadStatus[] = ["PROSPECT", "DEMO", "NEGOCIATION", "GAGNE"];

// ─── Components ───────────────────────────────────────────────────────────────

function ForecastBar({
  label,
  raw,
  weighted,
  maxValue,
  color,
}: {
  label: string;
  raw: number;
  weighted: number;
  maxValue: number;
  color: string;
}) {
  const rawPct = maxValue > 0 ? (raw / maxValue) * 100 : 0;
  const weightedPct = maxValue > 0 ? (weighted / maxValue) * 100 : 0;
  return (
    <div className="mb-4">
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          {weighted.toLocaleString("fr-FR")} € pondéré /{" "}
          <span className="text-muted-foreground/70">{raw.toLocaleString("fr-FR")} € brut</span>
        </span>
      </div>
      <div className="relative h-4 bg-muted rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full opacity-25 ${color}`}
          style={{ width: `${rawPct}%` }}
        />
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${color}`}
          style={{ width: `${weightedPct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesForecast() {
  const { data, isLoading } = trpc.sales.getForecast.useQuery();

  if (isLoading || !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </AdminLayout>
    );
  }

  const leads = data.leads;
  const activeLeads = leads.filter((l) => l.status !== "PERDU");

  // Compute stage breakdown from leads (using frontend STAGE_PROBABILITY for consistency)
  const stageBreakdown = FORECAST_STAGES.map((status) => {
    const stageLeads = leads.filter((l) => l.status === status);
    const raw = stageLeads.reduce((s, l) => s + l.estimatedValue, 0);
    const weighted = Math.round(raw * STAGE_PROBABILITY[status]);
    return { status, stageLeads, raw, weighted };
  });

  const totalRaw = stageBreakdown.reduce((s, b) => s + b.raw, 0);
  const totalWeighted = stageBreakdown.reduce((s, b) => s + b.weighted, 0);
  const maxValue = Math.max(...stageBreakdown.map((b) => b.raw), 1);

  const wonLeads = leads.filter(l => l.status === "GAGNE");
  const currentMRR = Math.round(wonLeads.reduce((s, l) => s + l.estimatedValue / 12, 0));
  const hasEnoughHistory = wonLeads.length >= 3;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp aria-hidden="true" className="h-6 w-6 text-primary" />
          Prévisions revenus
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Pipeline pondéré par probabilité de conversion
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-xs text-muted-foreground mb-1">Pipeline pondéré (ARR)</div>
            <div className="text-2xl font-bold text-primary">
              {totalWeighted.toLocaleString("fr-FR")} €
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              sur {totalRaw.toLocaleString("fr-FR")} € brut
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-xs text-muted-foreground mb-1">MRR actuel</div>
            <div className="text-2xl font-bold">
              {hasEnoughHistory ? `${currentMRR.toLocaleString("fr-FR")} €` : "—"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {hasEnoughHistory
                ? `basé sur ${wonLeads.length} deal${wonLeads.length > 1 ? "s" : ""} gagné${wonLeads.length > 1 ? "s" : ""}`
                : "min. 3 deals gagnés requis"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-xs text-muted-foreground mb-1">Leads en négociation</div>
            <div className="text-2xl font-bold">
              {leads.filter((l) => l.status === "NEGOCIATION").length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {leads
                .filter((l) => l.status === "NEGOCIATION")
                .reduce((s, l) => s + l.estimatedValue, 0)
                .toLocaleString("fr-FR")}{" "}
              € potentiel (65%)
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Pipeline pondéré par étape */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Euro aria-hidden="true" className="h-4 w-4" />
              Pipeline pondéré par étape
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stageBreakdown.map((b) => (
              <ForecastBar
                key={b.status}
                label={STAGE_CONFIG[b.status].label}
                raw={b.raw}
                weighted={b.weighted}
                maxValue={maxValue}
                color={BAR_COLORS[b.status] || "bg-primary"}
              />
            ))}
            <div className="border-t pt-3 mt-2">
              <div className="flex justify-between text-sm font-semibold">
                <span>Total pondéré</span>
                <span className="text-primary">{totalWeighted.toLocaleString("fr-FR")} €</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MRR breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Récap MRR — Deals gagnés</CardTitle>
          </CardHeader>
          <CardContent>
            {hasEnoughHistory ? (
              <>
                <div className="space-y-2 mb-4">
                  {wonLeads.map((lead) => {
                    const mrr = Math.round(lead.estimatedValue / 12);
                    return (
                      <div key={lead.id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{lead.name}</span>
                          <span className="text-muted-foreground ml-2 text-xs">{lead.company}</span>
                        </div>
                        <span className="font-mono text-xs">{mrr.toLocaleString("fr-FR")} €/mois</span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-sm font-semibold">
                    <span>MRR total</span>
                    <span className="text-primary">{currentMRR.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>ARR estimé</span>
                    <span>{(currentMRR * 12).toLocaleString("fr-FR")} €</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <TrendingUp className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm font-medium text-muted-foreground">Pas assez d'historique</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Au moins 3 deals gagnés sont nécessaires pour afficher une projection fiable.
                  Actuellement : {wonLeads.length} deal{wonLeads.length !== 1 ? "s" : ""}.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead detail table for forecast */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Détail leads actifs (hors perdus)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground text-xs">Lead</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground text-xs">Étape</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground text-xs">Valeur brute</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground text-xs">Prob.</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground text-xs">Pondéré</th>
              </tr>
            </thead>
            <tbody>
              {activeLeads.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                    Aucun lead actif
                  </td>
                </tr>
              )}
              {activeLeads.map((lead) => {
                const prob = STAGE_PROBABILITY[lead.status as LeadStatus] ?? 0;
                const weighted = Math.round(lead.estimatedValue * prob);
                const cfg = STAGE_CONFIG[lead.status as LeadStatus];
                return (
                  <tr key={lead.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-xs text-muted-foreground">{lead.company}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={cfg?.badgeVariant ?? "outline"} className="text-xs">
                        {cfg?.label.split(" (")[0] ?? lead.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">
                      {lead.estimatedValue.toLocaleString("fr-FR")} €
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`text-xs font-medium ${
                          prob >= 0.65 ? "text-success" : prob >= 0.3 ? "text-warning" : "text-muted-foreground"
                        }`}
                      >
                        {Math.round(prob * 100)}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium">
                      {weighted.toLocaleString("fr-FR")} €
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30">
                <td colSpan={4} className="px-4 py-2.5 text-sm font-semibold text-right">
                  Total pondéré
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-primary">
                  {totalWeighted.toLocaleString("fr-FR")} €
                </td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
